import { api } from "@/axios";

const dbName = "AppCacheDB";
const dbVersion = 1;
const storeName = "cache";

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, dbVersion);
    request.onerror = event => reject(event.target.error);
    request.onsuccess = event => resolve(event.target.result);
    request.onupgradeneeded = event => {
      const db = event.target.result;
      db.createObjectStore(storeName, { keyPath: "key" });
    };
  });
};

export const getCache = async key => {
  let db;
  try {
    db = await openDB();
    const transaction = db.transaction([storeName], "readonly");
    const getRequest = transaction.objectStore(storeName).get(key);
    return await new Promise((resolve, reject) => {
      getRequest.onerror = event => reject(event.target.error);
      getRequest.onsuccess = event => {
        const result = event.target.result;
        resolve(result ? result.data : null);
      };
    });
  } catch (error) {
    console.error(`Error reading cache for "${key}":`, error);
    return null;
  } finally {
    // an open connection would block deleteDatabase at login/logout
    if (db) db.close();
  }
};

export const setCache = async (key, data) => {
  let db;
  try {
    db = await openDB();
    const transaction = db.transaction([storeName], "readwrite");
    const putRequest = transaction
      .objectStore(storeName)
      .put({ key, data, cachedAt: Date.now() });
    await new Promise((resolve, reject) => {
      putRequest.onerror = event => reject(event.target.error);
      putRequest.onsuccess = () => resolve();
    });
  } catch (error) {
    console.error(`Error writing cache for "${key}":`, error);
  } finally {
    if (db) db.close();
  }
};

// Invalidates cached entries after a write (e.g. "services:" once a
// service/charge changes) so the next list call fetches fresh data
export const deleteCacheByPrefix = async prefix => {
  let db;
  try {
    db = await openDB();
    const transaction = db.transaction([storeName], "readwrite");
    const store = transaction.objectStore(storeName);
    const keysRequest = store.getAllKeys();
    await new Promise((resolve, reject) => {
      keysRequest.onerror = event => reject(event.target.error);
      keysRequest.onsuccess = () => {
        keysRequest.result
          .filter(key => String(key).startsWith(prefix))
          .forEach(key => store.delete(key));
        resolve();
      };
    });
  } catch (error) {
    console.error(`Error clearing cache for prefix "${prefix}":`, error);
  } finally {
    if (db) db.close();
  }
};

export const deleteCacheDB = () => {
  const request = indexedDB.deleteDatabase(dbName);
  request.onerror = event => {
    console.error("Error deleting cache database:", event.target.error);
  };
};

const REGISTRATION_ENDPOINTS = [
  { key: "services:all", url: "/service" },
  { key: "lookup:doctor", url: "/lookup?query=doctor" },
  { key: "lookup:hospital", url: "/lookup?query=hospital" },
  { key: "userDoctors", url: "/user/doctors" },
  { key: "branches", url: "/branch/index" }
];

// Fetches the registration page dropdown data (services, referred by,
// referred to) into IndexedDB, skipping anything already cached. Returns
// true once every endpoint is stored, so callers can retry until it is.
// onProgress(key) fires as each endpoint lands in the cache.
export const prefetchRegistrationCache = async onProgress => {
  const results = await Promise.allSettled(
    REGISTRATION_ENDPOINTS.map(async ({ key, url }) => {
      const cached = await getCache(key);
      if (!cached || cached.length === 0) {
        const res = await api.get(url);
        await setCache(key, res.data.result);
      }
      if (onProgress) onProgress(key);
    })
  );
  return results.every(r => r.status === "fulfilled");
};
